import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BottomNavigation } from '../components/BottomNavigation';
import { COLORS } from '../lib/styleGuide';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { getEnvironmentConfig } from '../lib/environment';
import { 
  Wallet,
  TickSquare,
  Danger,
  TimeCircle,
  ArrowLeft,
  ShieldDone
} from 'react-iconly';

// Style guide colors
const PRIMARY_BROWN = COLORS.PRIMARY_BROWN;
const NEUTRAL_BG = COLORS.NEUTRAL_BG;
const NEUTRAL_WHITE = COLORS.NEUTRAL_WHITE;
const NEUTRAL_TEXT = COLORS.NEUTRAL_TEXT;
const ACCENT_GRAY = COLORS.ACCENT_GRAY;
const SUCCESS_GREEN = COLORS.SUCCESS_GREEN;
const WARNING_ORANGE = COLORS.WARNING_ORANGE;
const ERROR_RED = COLORS.ERROR_RED;

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Bank account connection states
const CONNECTION_STATES = {
  NOT_CONNECTED: 'not_connected',
  PENDING: 'pending',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
  NEEDS_ACTION: 'needs_action'
};

export default function BankAccountConnection() {
  console.log('BankAccountConnection component loaded at:', new Date().toISOString());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { fetchStatus } = useOnboardingStatus();
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.NOT_CONNECTED);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [accountDetails, setAccountDetails] = useState(null);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      const { apiBaseUrl } = getEnvironmentConfig();
      const response = await fetch(`${apiBaseUrl}/getFinancialConnectionsStatus`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('BankAccountConnection: API Response:', data);
      console.log('BankAccountConnection: Status:', data.status);
      console.log('BankAccountConnection: Account Details:', data.accountDetails);
      
      // Map API status to component state
      let mappedStatus = data.status;
      if (data.status === 'connected') {
        mappedStatus = CONNECTION_STATES.VERIFIED;
      } else if (data.status === 'not_connected') {
        mappedStatus = CONNECTION_STATES.NOT_CONNECTED;
      }
      
      console.log('BankAccountConnection: Mapped Status:', mappedStatus);
      setConnectionState(mappedStatus);
      setAccountDetails(data.accountDetails);
    } catch (error) {
      console.error('Error fetching connection status:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        setError('Unable to connect to server. Please check if the backend is running.');
      } else {
        setError(`Failed to fetch connection status: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    // Handle return from Stripe onboarding
    const success = searchParams.get('success');
    const refresh = searchParams.get('refresh');
    
    if (success || refresh) {
      // Clear URL parameters
      navigate('/seller-hub/payments/bank', { replace: true });
      
      // Show success message if returning from successful onboarding
      if (success) {
        setError(''); // Clear any existing errors
        
        // Refresh onboarding status to update the checklist widget
        setTimeout(() => {
          fetchStatus();
        }, 1000); // Small delay to ensure the user document is updated
      }
    }
    
    fetchConnectionStatus();
  }, [searchParams, navigate, fetchConnectionStatus, fetchStatus]);

  // Reset bank connection (clear old Stripe Connect data)
  const resetBankConnection = async () => {
    try {
      setConnecting(true);
      setError('');
      
      const { apiBaseUrl } = getEnvironmentConfig();
      const response = await fetch(`${apiBaseUrl}/resetBankConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });

      if (response.ok) {
        console.log('Bank connection reset successfully');
        // Refresh the connection status
        await fetchConnectionStatus();
        setError('');
      } else {
        const errorData = await response.json();
        console.error('Failed to reset bank connection:', errorData);
        setError('Failed to reset bank connection. Please try again.');
      }
    } catch (error) {
      console.error('Error resetting bank connection:', error);
      setError('Failed to reset bank connection. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect bank account
  const disconnectBankAccount = async () => {
    try {
      setConnecting(true);
      setError('');
      
      const { apiBaseUrl } = getEnvironmentConfig();
      const response = await fetch(`${apiBaseUrl}/disconnectBankAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });

      if (response.ok) {
        console.log('Bank account disconnected successfully');
        // Refresh the connection status
        await fetchConnectionStatus();
        setError('');
      } else {
        const errorData = await response.json();
        console.error('Failed to disconnect bank account:', errorData);
        setError('Failed to disconnect bank account. Please try again.');
      }
    } catch (error) {
      console.error('Error disconnecting bank account:', error);
      setError('Failed to disconnect bank account. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Test Financial Connections with a simple approach
  const testFinancialConnectionsSimple = async () => {
    try {
      console.log('Testing Financial Connections with simple approach...');
      
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      console.log('Stripe instance created:', !!stripe);
      
      // Try to create a test session directly
      const { apiBaseUrl } = getEnvironmentConfig();
      const response = await fetch(`${apiBaseUrl}/createFinancialConnectionsSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Session created successfully:', data);
        
        // Try the collection with more detailed logging
        console.log('Attempting collectFinancialConnectionsAccounts...');
        const result = await stripe.collectFinancialConnectionsAccounts({
          clientSecret: data.clientSecret,
        });
        
        console.log('Collection result:', result);
        console.log('Result type:', typeof result);
        console.log('Result keys:', Object.keys(result || {}));
        
        if (result && result.session) {
          console.log('Session found:', result.session);
          console.log('Session status:', result.session.status);
        } else {
          console.log('No session in result');
        }
        
        if (result && result.error) {
          console.log('Error in result:', result.error);
        }
        
      } else {
        console.error('Failed to create session:', response.status);
      }
      
    } catch (error) {
      console.error('Test error:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
  };

  // Launch Stripe Connect Express (fallback due to Finicity issues)
  const launchStripeConnectExpress = async () => {
    try {
      console.log('BankAccountConnection: Launching Stripe Connect Express due to Finicity issues...');
      
      const { apiBaseUrl } = getEnvironmentConfig();
      const response = await fetch(`${apiBaseUrl}/connectBankAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stripe Connect Express response:', data);
        
        if (data.accountLinkUrl) {
          // Redirect to Stripe Connect Express onboarding
          window.location.href = data.accountLinkUrl;
        } else {
          setError('Failed to create bank account link. Please try again.');
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to create Stripe Connect Express link:', errorData);
        setError('Failed to create bank account link. Please try again.');
      }
    } catch (error) {
      console.error('Error launching Stripe Connect Express:', error);
      setError('Failed to open bank linking. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Launch Financial Connections modal with better error handling
  const launchFinancialConnectionsModal = async (clientSecret, sessionId) => {
    try {
      console.log('BankAccountConnection: Loading Stripe with key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.substring(0, 20) + '...');
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      console.log('BankAccountConnection: Stripe instance created:', !!stripe);
      
      console.log('BankAccountConnection: About to call collectFinancialConnectionsAccounts with clientSecret:', clientSecret?.substring(0, 20) + '...');
      
      try {
        const result = await stripe.collectFinancialConnectionsAccounts({
          clientSecret: clientSecret,
        });
        
        console.log('BankAccountConnection: Financial Connections response:', result);
        console.log('BankAccountConnection: Session status:', result?.session?.status);
        console.log('BankAccountConnection: Session accounts:', result?.session?.accounts);
        
        const { error, session } = result;

        if (error) {
          console.error('Financial Connections error:', error);
          setError(`Bank linking failed: ${error.message}`);
          setConnecting(false);
          return;
        }

        if (session && session.status === 'succeeded') {
          console.log('Financial Connections session succeeded:', session);
          
          // Attach the account to the user
          const { apiBaseUrl } = getEnvironmentConfig();
          console.log('BankAccountConnection: Calling attachFinancialConnectionsAccount with sessionId:', sessionId);
          const attachResponse = await fetch(`${apiBaseUrl}/attachFinancialConnectionsAccount`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await currentUser.getIdToken()}`
            },
            body: JSON.stringify({ sessionId: sessionId })
          });

          console.log('BankAccountConnection: Attach response status:', attachResponse.status);
          if (attachResponse.ok) {
            const attachData = await attachResponse.json();
            console.log('Account attached successfully:', attachData);
            
            // Refresh the connection status
            await fetchConnectionStatus();
            
            // Show success message
            setConnectionState(CONNECTION_STATES.VERIFIED);
            setError('');
          } else {
            const attachError = await attachResponse.json();
            console.error('Failed to attach account:', attachError);
            setError('Failed to save bank account. Please try again.');
          }
        } else if (!session && !error) {
          // This is the Finicity edge case - session is undefined but account might be created
          console.log('BankAccountConnection: Session is undefined (Finicity issue) - checking if account was created anyway');
          
          // Wait a moment for Stripe to process the account creation
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Try to attach the account using the sessionId anyway
          const { apiBaseUrl } = getEnvironmentConfig();
          console.log('BankAccountConnection: Attempting to attach account with sessionId:', sessionId);
          
          try {
            const attachResponse = await fetch(`${apiBaseUrl}/attachFinancialConnectionsAccount`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await currentUser.getIdToken()}`
              },
              body: JSON.stringify({ sessionId: sessionId })
            });

            console.log('BankAccountConnection: Attach response status:', attachResponse.status);
            if (attachResponse.ok) {
              const attachData = await attachResponse.json();
              console.log('Account attached successfully despite undefined session:', attachData);
              
              // Refresh the connection status
              await fetchConnectionStatus();
              
              // Check if it's a pending connection (Finicity delay)
              if (attachData.accountDetails?.status === 'pending_finicity_sync') {
                setConnectionState(CONNECTION_STATES.VERIFIED);
                setError('Bank account linking is processing. This may take a few minutes due to bank verification delays.');
              } else {
                // Show success message
                setConnectionState(CONNECTION_STATES.VERIFIED);
                setError('');
              }
            } else {
              const attachError = await attachResponse.json();
              console.error('Failed to attach account:', attachError);
              setError('Bank linking was not completed. Please try again.');
            }
          } catch (attachError) {
            console.error('Error during attach attempt:', attachError);
            setError('Bank linking was not completed. Please try again.');
          }
        } else {
          console.log('Financial Connections session not completed:', session);
          console.log('BankAccountConnection: Session status was:', session?.status);
          setError('Bank linking was not completed. Please try again.');
        }
        
      } catch (collectionError) {
        console.error('BankAccountConnection: Error during collectFinancialConnectionsAccounts:', collectionError);
        console.error('BankAccountConnection: Collection error details:', {
          message: collectionError.message,
          stack: collectionError.stack,
          name: collectionError.name
        });
        
        // If it's a Finicity-related error, try to attach the account anyway
        if (collectionError.message.includes('Malformed or undefined FI URL') || 
            collectionError.message.includes('connect2.finicity.com') ||
            collectionError.message.includes('r.stripe.com') ||
            collectionError.message.includes('Failed to fetch') ||
            collectionError.message.includes('ERR_FAILED') ||
            collectionError.message.includes('CORS policy') ||
            collectionError.message.includes('Access-Control-Allow-Origin')) {
          console.log('BankAccountConnection: Finicity/CORS error detected - attempting to attach account anyway');
          
          // Check if user tried to link a digital wallet
          if (collectionError.message.includes('r.stripe.com') || collectionError.message.includes('CORS')) {
            setError('⚠️ Digital wallets like Venmo cannot be linked for payouts. Please use your actual bank account. Only checking or savings accounts from a bank are supported.');
            setConnecting(false);
            return;
          }
          
          // Wait a moment for Stripe to process the account creation
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Try to attach the account using the sessionId
          const { apiBaseUrl } = getEnvironmentConfig();
          console.log('BankAccountConnection: Attempting to attach account with sessionId:', sessionId);
          
          try {
            const attachResponse = await fetch(`${apiBaseUrl}/attachFinancialConnectionsAccount`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await currentUser.getIdToken()}`
              },
              body: JSON.stringify({ sessionId: sessionId })
            });

            console.log('BankAccountConnection: Attach response status:', attachResponse.status);
            if (attachResponse.ok) {
              const attachData = await attachResponse.json();
              console.log('Account attached successfully despite Finicity error:', attachData);
              
              // Refresh the connection status
              await fetchConnectionStatus();
              
              // Show success message
              setConnectionState(CONNECTION_STATES.VERIFIED);
              setError('');
            } else {
              const attachError = await attachResponse.json();
              console.error('Failed to attach account:', attachError);
              setError('Bank linking was not completed. Please try again.');
            }
          } catch (attachError) {
            console.error('Error during attach attempt:', attachError);
            setError('Bank linking was not completed. Please try again.');
          }
          
          return;
        }
        
        setError(`Bank linking failed: ${collectionError.message}`);
        setConnecting(false);
        return;
      }
      
      setConnecting(false);
    } catch (error) {
      console.error('Error launching Financial Connections modal:', error);
      setError('Failed to open bank linking. Please try again.');
      setConnecting(false);
    }
  };

  const handleConnectBank = async () => {
    try {
      console.log('BankAccountConnection: handleConnectBank called');
      setConnecting(true);
      setError('');
      
      const { apiBaseUrl } = getEnvironmentConfig();
      console.log('BankAccountConnection: Calling API:', `${apiBaseUrl}/createFinancialConnectionsSession`);
      const response = await fetch(`${apiBaseUrl}/createFinancialConnectionsSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle Financial Connections registration error
        if (errorData.error === 'Financial Connections not available') {
          setError(`Financial Connections is not available. Please register at: ${errorData.registrationUrl}`);
          setConnecting(false);
          return;
        }
        
        throw new Error(errorData.error || 'Failed to start bank connection process');
      }
      
          const data = await response.json();
          console.log('BankAccountConnection: API response:', data);
          
          // Launch Financial Connections modal
          console.log('BankAccountConnection: Launching Financial Connections modal with client secret:', data.clientSecret);
          
          // Load Stripe.js if not already loaded
          if (!window.Stripe) {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.onload = () => launchFinancialConnectionsModal(data.clientSecret, data.sessionId);
            document.head.appendChild(script);
          } else {
            launchFinancialConnectionsModal(data.clientSecret, data.sessionId);
          }
      
    } catch (error) {
      console.error('Error connecting bank account:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        setError('Unable to connect to server. Please check if the backend is running.');
      } else {
        setError(error.message || 'Failed to start bank connection process');
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleUpdateBank = () => {
    handleConnectBank();
  };

  const handleRetryConnection = () => {
    handleConnectBank();
  };

  const handleResetBankAccount = async () => {
    try {
      setConnecting(true);
      setError('');
      
      // Remove the stripeAccountId from the user document
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        stripeAccountId: null,
        updatedAt: new Date().toISOString()
      });
      
      // Refresh the connection status
      await fetchConnectionStatus();
      
      console.log('Bank account reset successfully');
      
    } catch (error) {
      console.error('Error resetting bank account:', error);
      setError('Failed to reset bank account. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const renderConnectionState = () => {
    // Only show different state when account is actually verified/connected
    if (connectionState === CONNECTION_STATES.VERIFIED) {
      return (
        <div style={{
          background: NEUTRAL_WHITE,
          borderRadius: 16,
          padding: 32,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <TickSquare set="regular" size={64} primaryColor={SUCCESS_GREEN} />
          <h2 style={{ 
            ...headingFont, 
            fontSize: 24, 
            color: NEUTRAL_TEXT, 
            margin: '24px 0 16px 0' 
          }}>
            Bank Account Connected!
          </h2>
          <p style={{ 
            fontSize: 16, 
            color: ACCENT_GRAY, 
            marginBottom: 24,
            lineHeight: 1.5,
            ...bodyFont
          }}>
            Your bank account is verified and ready to receive payouts.
          </p>
          
          {accountDetails && (
            <div style={{
              background: NEUTRAL_BG,
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              textAlign: 'left'
            }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ 
                  fontSize: 14, 
                  color: ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Bank:
                </span>
                <span style={{ 
                  fontSize: 16, 
                  color: NEUTRAL_TEXT,
                  fontWeight: 600,
                  marginLeft: 8,
                  ...bodyFont
                }}>
                  {accountDetails.institution || accountDetails.bankName || 'Unknown Bank'}
                </span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <span style={{ 
                  fontSize: 14, 
                  color: ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Account:
                </span>
                <span style={{ 
                  fontSize: 16, 
                  color: NEUTRAL_TEXT,
                  fontWeight: 600,
                  marginLeft: 8,
                  ...bodyFont
                }}>
                  ****{accountDetails.last4}
                </span>
              </div>
              <div>
                <span style={{ 
                  fontSize: 14, 
                  color: ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Status:
                </span>
                <span style={{ 
                  fontSize: 16, 
                  color: SUCCESS_GREEN,
                  fontWeight: 600,
                  marginLeft: 8,
                  ...bodyFont
                }}>
                  Verified
                </span>
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={disconnectBankAccount}
              disabled={connecting}
              style={{
                background: 'none',
                color: ERROR_RED,
                border: `2px solid ${ERROR_RED}`,
                borderRadius: 12,
                padding: '14px 28px',
                fontSize: 16,
                fontWeight: 600,
                cursor: connecting ? 'not-allowed' : 'pointer',
                opacity: connecting ? 0.7 : 1,
                transition: 'all 0.2s ease',
                ...bodyFont
              }}
            >
              {connecting ? 'Disconnecting...' : 'Disconnect Bank'}
            </button>
          </div>
        </div>
      );
    }

    // For all other states (not_connected, pending, rejected, needs_action), show the basic connect message
    return (
      <div style={{
        background: NEUTRAL_WHITE,
        borderRadius: 16,
        padding: 32,
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Wallet set="regular" size={64} primaryColor={ACCENT_GRAY} />
        <h2 style={{ 
          ...headingFont, 
          fontSize: 24, 
          color: NEUTRAL_TEXT, 
          margin: '24px 0 16px 0' 
        }}>
          Link Your Bank Account
        </h2>
        <p style={{ 
          fontSize: 16, 
          color: ACCENT_GRAY, 
          marginBottom: 16,
          lineHeight: 1.5,
          ...bodyFont
        }}>
          Connect your bank account to receive payouts from your sales. This will open a secure modal to link your bank account.
        </p>
        
        <div style={{
          background: '#FFF3E0',
          border: '1px solid #FF9800',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 24,
          textAlign: 'left'
        }}>
          <span style={{ 
            fontSize: 13, 
            color: '#E65100',
            ...bodyFont
          }}>
            ⚠️ Only bank accounts are supported. Digital wallets like Venmo, PayPal, or Cash App cannot be linked for payouts. Please use your actual bank account.
          </span>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 8, 
          marginBottom: 32,
          padding: '16px 24px',
          background: NEUTRAL_BG,
          borderRadius: 12
        }}>
          <ShieldDone set="regular" size={20} primaryColor={SUCCESS_GREEN} />
          <span style={{ 
            fontSize: 14, 
            color: ACCENT_GRAY,
            ...bodyFont
          }}>
            Powered by Stripe • Bank-level security
          </span>
        </div>
        
        <button
          onClick={handleConnectBank}
          disabled={connecting}
          style={{
            background: COLORS.PRIMARY_GRADIENT,
            color: NEUTRAL_WHITE,
            border: 'none',
            borderRadius: 12,
            padding: '16px 32px',
            fontSize: 16,
            fontWeight: 600,
            cursor: connecting ? 'not-allowed' : 'pointer',
            opacity: connecting ? 0.7 : 1,
            transition: 'all 0.2s ease',
            ...bodyFont
          }}
        >
          {connecting ? 'Connecting...' : 'Link Bank Account'}
        </button>
        
        {/* Alternative: Use Stripe Connect Express (more reliable) */}
        <button
          onClick={launchStripeConnectExpress}
          disabled={connecting}
          style={{
            background: 'transparent',
            color: PRIMARY_BROWN,
            border: `2px solid ${PRIMARY_BROWN}`,
            borderRadius: 12,
            padding: '14px 28px',
            fontSize: 14,
            fontWeight: 600,
            cursor: connecting ? 'not-allowed' : 'pointer',
            opacity: connecting ? 0.7 : 1,
            transition: 'all 0.2s ease',
            marginTop: 12,
            ...bodyFont
          }}
        >
          Use Alternative Setup (Recommended)
        </button>
        
        {/* Reset button for users with old Stripe Connect data */}
        <button
          onClick={resetBankConnection}
          disabled={connecting}
          style={{
            background: 'transparent',
            color: ERROR_RED,
            border: `1px solid ${ERROR_RED}`,
            borderRadius: 12,
            padding: '12px 24px',
            fontSize: 14,
            fontWeight: 500,
            cursor: connecting ? 'not-allowed' : 'pointer',
            opacity: connecting ? 0.7 : 1,
            transition: 'all 0.2s ease',
            marginTop: 12,
            ...bodyFont
          }}
        >
          Reset Bank Connection
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: NEUTRAL_BG, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        paddingBottom: '80px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            border: `4px solid ${NEUTRAL_BG}`, 
            borderTop: `4px solid ${COLORS.PRIMARY_GRADIENT_START}`, 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: ACCENT_GRAY, fontSize: 16, ...bodyFont }}>
            Loading bank connection status...
          </p>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: NEUTRAL_BG, 
      paddingBottom: '80px' 
    }}>
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '16px 20px' 
        }}>
          <button
            onClick={() => navigate('/seller-hub')}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              marginRight: 16,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ArrowLeft set="regular" size={24} primaryColor={NEUTRAL_TEXT} />
          </button>
          <h1 style={{ 
            ...headingFont, 
            fontSize: 20, 
            color: NEUTRAL_TEXT, 
            margin: 0 
          }}>
            Bank Account Connection
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ paddingTop: '72px' }}>
        <div style={{ padding: '20px' }}>
          {error && (
            <div style={{
              background: '#FFEBEE',
              color: ERROR_RED,
              padding: '16px',
              borderRadius: 12,
              marginBottom: 20,
              border: `1px solid ${ERROR_RED}`,
              ...bodyFont
            }}>
              <div style={{ marginBottom: '12px' }}>
                {error}
              </div>
              {error.includes('Unable to connect to server') && (
                <button
                  onClick={fetchConnectionStatus}
                  style={{
                    background: COLORS.PRIMARY_GRADIENT,
                    color: NEUTRAL_WHITE,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Retry Connection
                </button>
              )}
            </div>
          )}
          
          {searchParams.get('success') && (
            <div style={{
              background: '#E8F5E9',
              color: SUCCESS_GREEN,
              padding: '16px',
              borderRadius: 12,
              marginBottom: 20,
              border: `1px solid ${SUCCESS_GREEN}`,
              ...bodyFont
            }}>
              <div style={{ marginBottom: '12px' }}>
                ✅ Bank account setup completed! We're reviewing your information.
              </div>
              <button
                onClick={() => {
                  fetchStatus();
                  setError('');
                }}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Update Checklist
              </button>
            </div>
          )}
          
          {renderConnectionState()}
        </div>
      </div>

      <BottomNavigation />
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
